// The moderator's own monthly fee and its payout request form.
import { FormEvent, useEffect, useMemo, useState } from "react";
import { payoutsApi } from "@/api/payouts.api";
import type { PaymentNetwork } from "@/lib/types/payments";
import { computeModeratorPayoutState } from "../logic/earnings-logic";
import { apiErrorMessage } from "../logic/payout-logic";
import type { GetPaidBase } from "./get-paid-context";

interface ModeratorEarningsInput extends GetPaidBase {
  monthClosed: boolean;
  moderatorAmount: number;
  loadMyPayoutRequests: () => Promise<void>;
}

export function useModeratorEarnings({ t, user, toast, monthClosed, moderatorAmount, loadMyPayoutRequests }: ModeratorEarningsInput) {
  const [moderatorAccumulatedInfo, setModeratorAccumulatedInfo] = useState<{ amount: number; lastPayoutDate?: string }>({ amount: 0 });
  const [moderatorRequestForm, setModeratorRequestForm] = useState<{ amount: string; phone: string; network: PaymentNetwork; description: string }>({
    amount: "",
    phone: user?.phoneNumber ?? "",
    network: "FLOOZ",
    description: "",
  });
  const [submittingModeratorRequest, setSubmittingModeratorRequest] = useState(false);

  const moderatorPayoutState = useMemo(() => {
    if (user?.role !== "moderator") return null;
    return computeModeratorPayoutState({
      contributionsCount: user.contributionsCount,
      userLastPayoutDate: user?.lastPayoutDate,
      accumulated: moderatorAccumulatedInfo,
      monthClosed,
      moderatorAmount,
    });
  }, [user, moderatorAmount, monthClosed, moderatorAccumulatedInfo]);

  useEffect(() => {
    if (user?.role !== "moderator") return;
    setModeratorRequestForm((prev) => ({
      ...prev,
      phone: user?.phoneNumber ?? prev.phone,
      amount: prev.amount || String(Math.max(0, Math.round(moderatorPayoutState?.earnedAmount ?? 0))),
    }));
  }, [user?.role, user?.phoneNumber, moderatorPayoutState?.earnedAmount]);

  useEffect(() => {
    if (user?.role !== "moderator" || !user?.id) {
      return;
    }
    void payoutsApi
      .getModeratorAccumulatedAmount(user.id)
      .then((result) => {
        setModeratorAccumulatedInfo({ amount: result.amount, lastPayoutDate: result.lastPayoutDate });
      })
      .catch(() => {
        setModeratorAccumulatedInfo({ amount: 0 });
      });
  }, [user?.role, user?.id]);

  const handleSubmitModeratorRequest = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (user?.role !== "moderator" || !user?.id) {
      toast({ title: t.getPaid.accessDenied, description: t.getPaid.moderatorOnly, variant: "destructive" });
      return;
    }
    if (!moderatorPayoutState?.eligible) {
      toast({ title: t.getPaid.amountUnavailable, description: t.getPaid.monthlyFeeUnavailable, variant: "destructive" });
      return;
    }

    const amount = Math.round(Number(moderatorPayoutState.earnedAmount));
    const phone = moderatorRequestForm.phone.trim();
    if (!Number.isFinite(amount) || amount <= 0) {
      toast({ title: t.amountInvalid, description: t.amountGreaterThanZero, variant: "destructive" });
      return;
    }
    if (!phone) {
      toast({ title: t.getPaid.phoneRequiredTitle, description: t.getPaid.phoneRequiredDesc, variant: "destructive" });
      return;
    }

    setSubmittingModeratorRequest(true);
    try {
      await payoutsApi.createModeratorPayoutRequest({
        moderatorId: String(user.id),
        amount,
        phone_number: phone,
        network: moderatorRequestForm.network,
        description: moderatorRequestForm.description.trim() || undefined,
      });
      toast({ title: t.payoutRequestSent, description: t.payoutRequestSentDesc });
      setModeratorRequestForm((prev) => ({ ...prev, amount: "", description: "" }));
      loadMyPayoutRequests();
    } catch (error: any) {
      toast({ title: "Erreur", description: apiErrorMessage(error, "Impossible de soumettre la demande"), variant: "destructive" });
    } finally {
      setSubmittingModeratorRequest(false);
    }
  };

  return { moderatorPayoutState, moderatorRequestForm, submittingModeratorRequest, handleSubmitModeratorRequest };
}
